import React, { useState, useEffect } from 'react';
import { Download, Copy, Check, Users, Mail, RefreshCw } from 'lucide-react';

const AdminNotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedAge, setCopiedAge] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/notifications`);
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Group subscribers by age
  const subscribersByAge = {};
  for (let age = 6; age <= 18; age++) {
    subscribersByAge[age] = [];
  }

  notifications.forEach(notif => {
    notif.selected_ages.forEach(age => {
      if (subscribersByAge[age]) {
        if (!subscribersByAge[age].includes(notif.email)) {
          subscribersByAge[age].push(notif.email);
        }
      }
    });
  });

  // Get all unique emails
  const allEmails = [...new Set(notifications.map(n => n.email))];

  const copyEmails = (emails, age) => {
    navigator.clipboard.writeText(emails.join(', '));
    setCopiedAge(age);
    setTimeout(() => setCopiedAge(null), 2000);
  };

  const exportCSV = () => {
    let csv = 'Email,Ages Subscribed,Date Subscribed\n';
    notifications.forEach(notif => {
      const date = new Date(notif.created_at).toLocaleDateString();
      csv += `${notif.email},"${notif.selected_ages.join(', ')}",${date}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ei-academy-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportByAge = (age, emails) => {
    let csv = 'Email\n';
    emails.forEach(email => {
      csv += `${email}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ei-academy-age-${age}-subscribers.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-page)' }}>
      <div className="container py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="heading-hero mb-2">Notification Subscribers</h1>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              Manage email subscribers for upcoming book launches
            </p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={fetchNotifications}
              className="btn-secondary flex items-center gap-2"
              disabled={loading}
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button 
              onClick={exportCSV}
              className="btn-primary flex items-center gap-2"
              disabled={notifications.length === 0}
            >
              <Download size={18} />
              Export All CSV
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-blue-200)' }}>
                <Users size={24} style={{ color: 'var(--brand-primary)' }} />
              </div>
              <div>
                <p className="caption" style={{ color: 'var(--text-secondary)' }}>Total Subscribers</p>
                <p className="heading-2">{allEmails.length}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-green-200)' }}>
                <Mail size={24} style={{ color: 'var(--brand-success)' }} />
              </div>
              <div>
                <p className="caption" style={{ color: 'var(--text-secondary)' }}>Total Subscriptions</p>
                <p className="heading-2">{notifications.reduce((acc, n) => acc + n.selected_ages.length, 0)}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-orange-200)' }}>
                <RefreshCw size={24} style={{ color: 'var(--brand-orange)' }} />
              </div>
              <div>
                <p className="caption" style={{ color: 'var(--text-secondary)' }}>Last Updated</p>
                <p className="body-medium font-semibold">{lastRefresh.toLocaleTimeString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* All Emails Section */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading-3">All Subscriber Emails</h2>
            <button
              onClick={() => copyEmails(allEmails, 'all')}
              className="btn-secondary flex items-center gap-2 text-sm"
              disabled={allEmails.length === 0}
            >
              {copiedAge === 'all' ? <Check size={16} /> : <Copy size={16} />}
              {copiedAge === 'all' ? 'Copied!' : 'Copy All'}
            </button>
          </div>
          <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)', maxHeight: '150px', overflowY: 'auto' }}>
            {allEmails.length > 0 ? (
              <p className="body-small" style={{ color: 'var(--text-secondary)', wordBreak: 'break-word' }}>
                {allEmails.join(', ')}
              </p>
            ) : (
              <p className="body-small" style={{ color: 'var(--text-tertiary)' }}>No subscribers yet</p>
            )}
          </div>
        </div>

        {/* Subscribers by Age */}
        <h2 className="heading-2 mb-6">Subscribers by Age</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(subscribersByAge).map(([age, emails]) => (
            <div key={age} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                    style={{ 
                      background: emails.length > 0 ? 'var(--gradient-primary)' : 'var(--bg-section)',
                      color: emails.length > 0 ? 'white' : 'var(--text-tertiary)'
                    }}
                  >
                    {age}
                  </div>
                  <div>
                    <p className="body-medium font-semibold">Age {age}</p>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>
                      {emails.length} subscriber{emails.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyEmails(emails, age)}
                    className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                    disabled={emails.length === 0}
                    title="Copy emails"
                  >
                    {copiedAge === age ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                  </button>
                  <button
                    onClick={() => exportByAge(age, emails)}
                    className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                    disabled={emails.length === 0}
                    title="Export CSV"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </div>
              
              {emails.length > 0 ? (
                <div className="p-3 rounded-lg" style={{ background: 'var(--bg-section)', maxHeight: '120px', overflowY: 'auto' }}>
                  {emails.map((email, idx) => (
                    <p key={idx} className="body-small mb-1" style={{ color: 'var(--text-secondary)' }}>
                      {email}
                    </p>
                  ))}
                </div>
              ) : (
                <div className="p-3 rounded-lg text-center" style={{ background: 'var(--bg-section)' }}>
                  <p className="body-small" style={{ color: 'var(--text-tertiary)' }}>No subscribers yet</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Recent Subscriptions Table */}
        <h2 className="heading-2 mt-12 mb-6">Recent Subscriptions</h2>
        <div className="card overflow-hidden" style={{ padding: 0 }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: 'var(--bg-section)' }}>
                  <th className="text-left p-4 body-small font-semibold">Email</th>
                  <th className="text-left p-4 body-small font-semibold">Ages Subscribed</th>
                  <th className="text-left p-4 body-small font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {notifications.length > 0 ? (
                  notifications.slice().reverse().map((notif, idx) => (
                    <tr key={idx} className="border-t" style={{ borderColor: 'var(--border-input)' }}>
                      <td className="p-4 body-small">{notif.email}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {notif.selected_ages.sort((a, b) => a - b).map(age => (
                            <span 
                              key={age} 
                              className="px-2 py-1 rounded-full text-xs font-medium"
                              style={{ background: 'var(--accent-blue-200)' }}
                            >
                              Age {age}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4 body-small" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(notif.created_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="p-8 text-center body-small" style={{ color: 'var(--text-tertiary)' }}>
                      No subscriptions yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationsPage;
